export const UTM_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmParamName = typeof UTM_PARAM_NAMES[number];
export type UtmParams = Partial<Record<UtmParamName, string>>;

const STORAGE_KEY = 'apply_site_utm_params';
const MAX_VALUE_LENGTH = 200;

function safeGetItem(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeSetItem(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // sessionStorage unavailable or quota exceeded — degrade silently.
  }
}

export function captureUtmParamsFromUrl(searchParams: URLSearchParams): void {
  const next: UtmParams = {};
  for (const name of UTM_PARAM_NAMES) {
    const raw = searchParams.get(name);
    if (raw && raw.length > 0) {
      next[name] = raw.slice(0, MAX_VALUE_LENGTH);
    }
  }
  if (Object.keys(next).length === 0) {
    return; // No UTMs present — leave storage untouched.
  }
  safeSetItem(JSON.stringify(next));
}

export function getStoredUtmParams(): UtmParams {
  const raw = safeGetItem();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as UtmParams;
    }
    return {};
  } catch {
    return {};
  }
}

export function appendUtmParamsToFormData(formData: FormData): void {
  const stored = getStoredUtmParams();
  for (const name of UTM_PARAM_NAMES) {
    const value = stored[name];
    if (typeof value === 'string' && value.length > 0) {
      formData.set(name, value);
    }
  }
}
